// src/screens/VehicleEntry.js v1.4
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getTenantByPlate, getActiveVehicleByPlate, getActiveVehicles,
  getMouCompanies, getSettings, logVehicleEntry, addMouReceivable,
  getOccupancy, isTenantTimeViolation, buildWhatsAppMessage, renewTenant,
  getCustomerByPlate, getCustomerStats
} from '../database/db';
import { format } from 'date-fns';
import { Mic, AlertCircle, CheckCircle, Info, Printer, ArrowLeft, Delete } from 'lucide-react';
import { getPrinterDevice, printEntryReceipt } from '../utils/bluetooth-printer';
import QRCode from 'qrcode';

const PKG_TYPES = [
  {value:'hourly',label:'⏱️ Hourly'},
  {value:'overnight',label:'🌙 Overnight'},
  {value:'weekly',label:'📅 Weekly'},
  {value:'biweekly',label:'📅 Bi-Weekly'},
  {value:'mou',label:'🤝 MOU Corporate'},
];
const PKG_TYPE_TENANT = [
  {value:'monthly-24hr',label:'Monthly 24hr'},
  {value:'monthly-night',label:'Monthly Night Only'},
  {value:'monthly-day',label:'Monthly Day Only'},
  {value:'weekly',label:'Weekly'},
  {value:'biweekly',label:'Bi-Weekly'}
];
const fmtDate = iso => { try { return format(new Date(iso),'dd/MM/yyyy'); } catch { return '—'; } };
const isExpired = iso => !iso || new Date(iso) < new Date();
const getLabel = pt => ({'monthly-24hr':'Monthly 24hr','monthly-night':'Monthly Night','monthly-day':'Monthly Day','weekly':'Weekly','biweekly':'Bi-Weekly','hourly':'Hourly','overnight':'Overnight','mou':'MOU'}[pt]||pt||'Hourly');
const minExpiry = () => { const d = new Date(); d.setDate(d.getDate()+1); return d.toISOString().split('T')[0]; };

// Change #5 — Plate Keypad Component
const KEYS_NUMS = ['1','2','3','4','5','6','7','8','9','0'];
const KEYS_LETTERS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'];

function PlateKeypad({ value, onChange, onConfirm, onClose }) {
  const [mode, setMode] = useState('num'); // 'num' | 'alpha'
  const tap = (ch) => onChange(value + ch);
  const del = () => onChange(value.slice(0,-1));
  const clear = () => onChange('');
  return (
    <div className="keypad-overlay" onClick={onClose}>
      <div className="keypad-container" onClick={e=>e.stopPropagation()}>
        <div className="flex-between" style={{marginBottom:8}}>
          <span style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>TAP TO ENTER PLATE</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>
        <div className="keypad-display">{value || <span style={{color:'var(--text-muted)'}}>DUBAI A 12345</span>}</div>
        <div className="flex-row" style={{marginBottom:10,gap:8}}>
          <button onClick={()=>setMode('num')} className="btn btn-sm" style={{flex:1,background:mode==='num'?'var(--accent)':'var(--bg-elevated)',color:mode==='num'?'#000':'var(--text-secondary)'}}>123 Numbers</button>
          <button onClick={()=>setMode('alpha')} className="btn btn-sm" style={{flex:1,background:mode==='alpha'?'var(--accent)':'var(--bg-elevated)',color:mode==='alpha'?'#000':'var(--text-secondary)'}}>ABC Letters</button>
          <button onClick={()=>tap(' ')} className="btn btn-sm btn-outline" style={{flex:1}}>SPACE</button>
        </div>
        {mode==='num' && (
          <div className="keypad-grid" style={{gridTemplateColumns:'repeat(5,1fr)'}}>
            {KEYS_NUMS.map(k=><button key={k} className="keypad-btn num" onClick={()=>tap(k)}>{k}</button>)}
          </div>
        )}
        {mode==='alpha' && (
          <div className="keypad-grid" style={{gridTemplateColumns:'repeat(7,1fr)'}}>
            {KEYS_LETTERS.map(k=><button key={k} className="keypad-btn letter" onClick={()=>tap(k)}>{k}</button>)}
          </div>
        )}
        <div className="keypad-actions" style={{marginTop:8}}>
          <button className="keypad-btn action" onClick={del}><Delete size={16}/></button>
          <button className="keypad-btn action" onClick={clear} style={{fontSize:13,background:'var(--danger)',color:'white',fontWeight:700}}>✕ CLEAR</button>
          <button className="keypad-btn confirm" onClick={onConfirm}>✓ Confirm</button>
        </div>
      </div>
    </div>
  );
}

export default function VehicleEntry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plate, setPlate] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('input');
  const [tenantData, setTenantData] = useState(null);
  const [pkgType, setPkgType] = useState('hourly');
  const [mouCompany, setMouCompany] = useState('');
  const [mouEvidenceChecked, setMouEvidenceChecked] = useState(false);
  const [customRate, setCustomRate] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [carType, setCarType] = useState('closed');
  const [alert, setAlert] = useState(null);
  const [listening, setListening] = useState(false);
  const [renewExpiryDate, setRenewExpiryDate] = useState('');
  const [renewPkgType, setRenewPkgType] = useState('');
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [entryRecord, setEntryRecord] = useState(null);
  const [entryQr, setEntryQr] = useState('');
  const [overnightRate, setOvernightRate] = useState('');
  const [overnightEndTime, setOvernightEndTime] = useState('07:00');
  // Change #9 — overnight advance
  const [overnightAdvance, setOvernightAdvance] = useState('');
  // Change #5 — keypad
  const [showKeypad, setShowKeypad] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Change #1 — WhatsApp phone on receipt
  const [receiptPhone, setReceiptPhone] = useState('');
  const [btPrinting, setBtPrinting] = useState(false);
  const [btAlert, setBtAlert] = useState('');
  const settings = getSettings();
  const mouCompanies = getMouCompanies().filter(m=>m.active);

  const checkPlate = () => {
    const p = plate.trim().toUpperCase();
    if (!p) { setAlert({type:'danger',msg:'Enter a plate number.'}); return; }
    const alreadyIn = getActiveVehicleByPlate(p);
    if (alreadyIn) { setAlert({type:'danger',msg:`${p} is already parked. Process exit first.`}); return; }
    const occ = getOccupancy();
    if (occ.overCapacity) setAlert({type:'warning',msg:`⚠️ Over capacity (${occ.occupied}/${occ.total}). Entry still allowed.`});
    else setAlert(null);
    // B6: Auto-fill phone from customer directory
    const knownCustomer = getCustomerByPlate(p);
    if (knownCustomer && !phone) setPhone(knownCustomer.phone||'');
    const tenant = getTenantByPlate(p);
    if (tenant) {
      if (tenant.plates && tenant.plates.length > 1) {
        const other = getActiveVehicles().find(v => tenant.plates.includes(v.plate) && v.plate !== p);
        if (other) { setAlert({type:'danger',msg:`⛔ Another vehicle (${other.plate}) from this package is already inside.`}); return; }
      }
      const now = new Date();
      const nowStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
      if (isTenantTimeViolation(tenant, nowStr))
        setAlert({type:'warning',msg:`⚠️ Outside allowed hours (${tenant.allowedStartTime}–${tenant.allowedEndTime}). Logged as violation.`});
      setTenantData(tenant); setStep('tenant');
    } else setStep('package');
  };

  const handleVoice = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      setAlert({type:'warning',msg:'Voice not supported on this browser/connection. Use keypad or type manually.'}); return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.lang='en-AE';
    r.onstart=()=>setListening(true);
    r.onresult=e=>{setPlate(e.results[0][0].transcript.toUpperCase().replace(/\s/g,''));setListening(false);};
    r.onerror=()=>setListening(false); r.onend=()=>setListening(false);
    r.start();
  };

  const handleTenantEntry = () => {
    if (isExpired(tenantData.expiryDate)) { setShowRenewModal(true); return; }
    confirmEntry({tenantId:tenantData.id,packageType:tenantData.packageType});
  };

  const handleRenewal = () => {
    if (!renewExpiryDate) { setAlert({type:'danger',msg:'Select new expiry date.'}); return; }
    if (new Date(renewExpiryDate) <= new Date()) { setAlert({type:'danger',msg:'Expiry date must be in the future.'}); return; }
    const pkgToUse = renewPkgType || tenantData.packageType;
    renewTenant(tenantData.id, renewExpiryDate, user.id);
    const updated = getTenantByPlate(plate.toUpperCase());
    setTenantData(updated); setShowRenewModal(false); setAlert(null);
    confirmEntry({tenantId:updated.id,packageType:pkgToUse});
  };

  const handleMouEntry = () => {
    if (!mouCompany) { setAlert({type:'danger',msg:'Select MOU company.'}); return; }
    if (!mouEvidenceChecked) { setAlert({type:'danger',msg:'Confirm shop bill evidence verified.'}); return; }
    const company = mouCompanies.find(m=>m.id===mouCompany);
    confirmEntry({isMou:true,mouId:mouCompany,mouCompanyName:company?.companyName,packageType:'mou',hourlyRate:company?.ratePerHour||5});
  };

  const handlePackageEntry = () => {
    if (pkgType==='mou') { setStep('mou'); return; }
    if (pkgType==='overnight') {
      if (!overnightRate) { setAlert({type:'danger',msg:'Enter agreed overnight rate.'}); return; }
      confirmEntry({packageType:'overnight',overnightRate:parseFloat(overnightRate),overnightEndTime,overnightAdvance:parseFloat(overnightAdvance)||0,hourlyRate:settings.hourlyRate,discountAmount:parseFloat(discountAmount)||0});
      return;
    }
    confirmEntry({packageType:pkgType,hourlyRate:customRate?parseFloat(customRate):settings.hourlyRate,discountAmount:parseFloat(discountAmount)||0});
  };

  const confirmEntry = async (extra) => {
    if (submitting) return; // prevent double tap
    setSubmitting(true);
    try {
    const entry = await logVehicleEntry({plate:plate.trim().toUpperCase(),entryTime:new Date().toISOString(),carType,staffId:user.id,staffName:user.name,customerPhone:phone||receiptPhone,...extra});
    if (extra.isMou) await addMouReceivable({vehicleId:entry.id,mouId:extra.mouId,mouCompanyName:extra.mouCompanyName,plate:plate.trim().toUpperCase(),entryTime:entry.entryTime,date:new Date().toISOString().split('T')[0],staffId:user.id});
    if(!entry){
      setAlert({type:'danger',msg:'Failed to save entry. Check connection and try again.'});
      setSubmitting(false);
      return;
    }
    if(entry.duplicate){
      setAlert({type:'danger',msg:entry.error||'This plate is already parked.'});
      setSubmitting(false);
      return;
    }
    const qr = await QRCode.toDataURL(JSON.stringify({plate:entry.plate,entry:entry.entryTime,type:extra.packageType||'hourly',staff:user.name})).catch(()=>'');
    setEntryQr(qr); setEntryRecord(entry); setReceiptPhone(phone); setStep('done'); setSubmitting(false);
    } catch(err) { setAlert({type:'danger',msg:'Entry failed. Please try again.'}); setSubmitting(false); }
  };

  const reset = () => {
    setPlate(''); setPhone(''); setStep('input'); setTenantData(null); setPkgType('hourly');
    setMouCompany(''); setMouEvidenceChecked(false); setCustomRate(''); setDiscountAmount('');
    setCarType('closed'); setAlert(null); setRenewExpiryDate(''); setRenewPkgType('');
    setShowRenewModal(false); setEntryRecord(null); setEntryQr('');
    setOvernightRate(''); setOvernightEndTime('07:00'); setOvernightAdvance('');
    setShowKeypad(false); setReceiptPhone('');
  };

  // Change #1 — WhatsApp always available, uses receiptPhone or lets staff enter
  const sendWhatsApp = (phoneNum) => {
    if (!entryRecord) return;
    const msg = buildWhatsAppMessage('entry',{
      date:format(new Date(entryRecord.entryTime),'dd/MM/yyyy'),
      entryTime:format(new Date(entryRecord.entryTime),'hh:mm a'),
      plate:entryRecord.plate,packageType:getLabel(entryRecord.packageType),
      rate:(!entryRecord.tenantId&&!entryRecord.isMou)?entryRecord.hourlyRate||settings.hourlyRate:null,
      advance:entryRecord.overnightAdvance>0?entryRecord.overnightAdvance.toFixed(2):null
    });
    const num = phoneNum.replace(/\D/g,'');
    const wa = num.startsWith('0')?'971'+num.slice(1):num.startsWith('971')?num:'971'+num;
    window.open(`https://wa.me/${wa}?text=${msg}`,'_blank');
  };

  const expired = tenantData && isExpired(tenantData.expiryDate);

  const CarTypeSel = () => (
    <div className="form-group">
      <label className="form-label">Car Type</label>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
        {[{v:'open',l:'◯ Open',c:'var(--success)'},{v:'closed',l:'● Closed',c:'var(--warning)'}].map(o=>(
          <button key={o.v} onClick={()=>setCarType(o.v)}
            style={{padding:12,borderRadius:'var(--radius-sm)',border:`2px solid ${carType===o.v?o.c:'var(--border)'}`,background:carType===o.v?`${o.c}20`:'var(--bg-input)',color:carType===o.v?o.c:'var(--text-secondary)',fontWeight:600,cursor:'pointer',fontSize:14}}>
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );

  const btPrint = async() => {
    const device = getPrinterDevice();
    if(!device){setBtAlert('Printer not connected. Go to Settings → Printer.');setTimeout(()=>setBtAlert(''),4000);return;}
    setBtPrinting(true);
    try{await printEntryReceipt(device,entryRecord,settings);setBtAlert('✅ Printed!');}
    catch(e){setBtAlert('❌ '+e.message);}
    setBtPrinting(false);
    setTimeout(()=>setBtAlert(''),4000);
  };

  return (
    <div className="fade-in">
      {/* Change #5 — Keypad overlay */}
      {showKeypad && (
        <PlateKeypad
          value={plate}
          onChange={setPlate}
          onConfirm={()=>{setShowKeypad(false);checkPlate();}}
          onClose={()=>setShowKeypad(false)}
        />
      )}

      <div className="page-header">
        <div><div className="page-title">🚗 Vehicle Entry</div><div className="page-subtitle">Log a vehicle entering</div></div>
        {step!=='input'&&<button className="btn btn-outline btn-sm" onClick={reset}>New Entry</button>}
      </div>
      <div className="page-body">
        {alert&&<div className={`alert alert-${alert.type}`}><AlertCircle size={14} style={{flexShrink:0}}/>{alert.msg}</div>}

        {step==='input'&&(
          <div style={{maxWidth:500,margin:'0 auto'}}>
            <div className="card">
              <div className="card-title">Enter Plate Number</div>
              {/* Change #5 — Tap field opens keypad */}
              <div className="form-group">
                <div style={{position:'relative'}}>
                  <div className="form-input plate-input" onClick={()=>setShowKeypad(true)} style={{minHeight:54,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',background:'var(--bg-input)',borderColor:showKeypad?'var(--accent)':'var(--border)',paddingRight:plate?48:16}}>
                    {plate || <span style={{color:'var(--text-muted)',fontSize:14,fontWeight:400}}>Tap to enter plate</span>}
                  </div>
                  {plate&&<button onClick={()=>{setPlate('');setAlert(null);}} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'var(--danger)',border:'none',cursor:'pointer',color:'white',width:36,height:36,borderRadius:6,fontSize:18,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Customer Phone (for WhatsApp)</label>
                <input className="form-input" type="tel" placeholder="+971 50 000 0000" value={phone} onChange={e=>setPhone(e.target.value)}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                <button className="btn btn-outline btn-full" onClick={()=>setShowKeypad(true)}>⌨️ Keypad</button>
                <button className="btn btn-outline btn-full" onClick={handleVoice} style={{color:listening?'var(--danger)':''}}>
                  <Mic size={14}/> {listening?'🔴 Listening...':'🎤 Voice'}
                </button>
              </div>
              {plate&&(
                <button className="btn btn-danger btn-full btn-sm" style={{marginBottom:8}} onClick={()=>{setPlate('');setAlert(null);}}>
                  ✕ Clear Plate Entry
                </button>
              )}
              <button className="btn btn-primary btn-full btn-lg" onClick={checkPlate}>Check Plate →</button>
            </div>
          </div>
        )}

        {step==='tenant'&&tenantData&&(
          <div style={{maxWidth:500,margin:'0 auto'}}>
            <div className="card">
              <div style={{textAlign:'center',marginBottom:14}}>
                <div style={{fontFamily:'var(--font-mono)',fontSize:22,fontWeight:700,color:'var(--accent)',letterSpacing:'0.1em'}}>{plate.toUpperCase()}</div>
                <span className={`badge ${expired?'badge-danger':'badge-success'}`} style={{marginTop:6,display:'inline-flex'}}>{expired?'EXPIRED':'ACTIVE'}</span>
              </div>
              <div style={{background:'var(--bg-input)',borderRadius:'var(--radius-sm)',padding:'12px',marginBottom:14}}>
                {[['Customer',tenantData.name],['Package',getLabel(tenantData.packageType)],
                  ['Phone',tenantData.phone||'—'],['Expiry',fmtDate(tenantData.expiryDate)],
                  ...(tenantData.allowedStartTime?[['Allowed',`${tenantData.allowedStartTime}–${tenantData.allowedEndTime}`]]:[])
                ].map(([k,v])=>(
                  <div key={k} className="flex-between" style={{marginBottom:6}}>
                    <span style={{fontSize:11,color:'var(--text-muted)'}}>{k}</span>
                    <span style={{fontSize:13,fontWeight:600,color:k==='Expiry'&&expired?'var(--danger)':'var(--text-primary)'}}>{v}</span>
                  </div>
                ))}
              </div>
              <CarTypeSel/>
              {expired
                ?<><div className="alert alert-danger"><AlertCircle size={14}/>Package expired. Renewal required.</div><button className="btn btn-warning btn-full btn-lg" onClick={()=>setShowRenewModal(true)}>Renew & Enter →</button></>
                :<button className="btn btn-success btn-full btn-lg" onClick={handleTenantEntry} disabled={submitting}>{submitting?'⏳ Saving...':'✅ Allow Entry'}</button>
              }
            </div>
          </div>
        )}

        {step==='package'&&(
          <div style={{maxWidth:500,margin:'0 auto'}}>
            <div className="card">
              <div style={{textAlign:'center',marginBottom:14}}>
                <div style={{fontFamily:'var(--font-mono)',fontSize:22,fontWeight:700,color:'var(--accent)',letterSpacing:'0.1em'}}>{plate.toUpperCase()}</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>New / Transient Vehicle</div>
              </div>
              <div className="form-group">
                <label className="form-label">Package Type</label>
                <select className="form-select" value={pkgType} onChange={e=>setPkgType(e.target.value)}>
                  {PKG_TYPES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              {pkgType==='overnight'&&(
                <>
                  <div className="alert alert-info"><Info size={14}/>Flat rate for agreed hours. Extra time beyond end time charged at AED {settings.hourlyRate}/hr.</div>
                  <div className="form-group"><label className="form-label">Agreed Overnight Rate (AED) *</label><input className="form-input" type="number" placeholder="e.g. 20" value={overnightRate} onChange={e=>setOvernightRate(e.target.value)}/></div>
                  <div className="form-group"><label className="form-label">Agreed End Time</label><input className="form-input" type="time" value={overnightEndTime} onChange={e=>setOvernightEndTime(e.target.value)}/></div>
                  {/* Change #9 — Advance collected */}
                  <div className="form-group">
                    <label className="form-label">Advance Collected (AED)</label>
                    <input className="form-input" type="number" placeholder="Amount collected now" value={overnightAdvance} onChange={e=>setOvernightAdvance(e.target.value)}/>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Enter the amount collected at entry. Balance (if any extra hours) collected at exit.</div>
                  </div>
                </>
              )}
              {pkgType!=='mou'&&pkgType!=='overnight'&&(
                <div className="form-group">
                  <label className="form-label">Hourly Rate (AED) {settings.rateEditingLocked?'🔒':''}</label>
                  <input className="form-input" type="number" placeholder={`Default: AED ${settings.hourlyRate}`} value={customRate} onChange={e=>setCustomRate(e.target.value)} disabled={settings.rateEditingLocked}/>
                </div>
              )}
              {pkgType!=='mou'&&(
                <div className="form-group">
                  <label className="form-label">Discount (AED) — optional</label>
                  <input className="form-input" type="number" placeholder="0.00" value={discountAmount} onChange={e=>setDiscountAmount(e.target.value)}/>
                  {discountAmount>0&&<div style={{fontSize:11,color:'var(--warning)',marginTop:4}}>⚠️ Logged for admin review</div>}
                </div>
              )}
              <CarTypeSel/>
              {pkgType==='mou'
                ?<button className="btn btn-primary btn-full btn-lg" onClick={()=>setStep('mou')}>Next: MOU Details →</button>
                :<button className="btn btn-success btn-full btn-lg" onClick={handlePackageEntry} disabled={submitting}>{submitting?'⏳ Saving...':'✅ Log Entry'}</button>
              }
            </div>
          </div>
        )}

        {step==='mou'&&(
          <div style={{maxWidth:500,margin:'0 auto'}}>
            <div className="card">
              <div style={{textAlign:'center',marginBottom:14}}>
                <div style={{fontFamily:'var(--font-mono)',fontSize:22,fontWeight:700,color:'var(--accent)'}}>{plate.toUpperCase()}</div>
                <span className="badge badge-mou" style={{marginTop:6,display:'inline-flex'}}>MOU</span>
              </div>
              <div className="alert alert-info"><Info size={14}/>MOU billed to company. Collect shop bill evidence.</div>
              <div className="form-group">
                <label className="form-label">Select MOU Company</label>
                <select className="form-select" value={mouCompany} onChange={e=>setMouCompany(e.target.value)}>
                  <option value="">— Select —</option>
                  {mouCompanies.map(m=><option key={m.id} value={m.id}>{m.companyName} (AED {m.ratePerHour}/hr)</option>)}
                </select>
              </div>
              <CarTypeSel/>
              <label className="checkbox-group" style={{marginBottom:14}}>
                <input type="checkbox" checked={mouEvidenceChecked} onChange={e=>setMouEvidenceChecked(e.target.checked)}/>
                <span className="checkbox-label"><strong>Shop bill / evidence verified</strong> before entry.</span>
              </label>
              <button className="btn btn-success btn-full btn-lg" onClick={handleMouEntry} disabled={submitting}>{submitting?'⏳ Saving...':'✅ Log MOU Entry'}</button>
            </div>
          </div>
        )}

        {step==='done'&&entryRecord&&(
          <div style={{maxWidth:420,margin:'0 auto'}}>
            <div className="alert alert-success"><CheckCircle size={14}/>Entry logged.</div>
            <div className="receipt">
              <div className="receipt-header">
                <div className="receipt-title">AL BARAKAH CAR PARKING</div>
                <div style={{fontSize:10,color:'#555'}}>Arabian Gulf Street, Near NMC Royal Hospital, Sharjah</div>
                <div style={{fontSize:10,fontWeight:700,marginTop:2}}>— ENTRY RECEIPT —</div>
              </div>
              <hr className="receipt-divider"/>
              <div className="receipt-row"><span>Date:</span><span>{format(new Date(entryRecord.entryTime),'dd/MM/yyyy')}</span></div>
              <div className="receipt-row"><span>Entry Time:</span><span style={{fontWeight:700}}>{format(new Date(entryRecord.entryTime),'hh:mm a')}</span></div>
              <div className="receipt-row"><span>Plate No.:</span><span style={{fontWeight:700}}>{entryRecord.plate}</span></div>
              <div className="receipt-row"><span>Package:</span><span>{entryRecord.isMou?`MOU — ${entryRecord.mouCompanyName}`:getLabel(entryRecord.packageType)}</span></div>
              <div className="receipt-row"><span>Car Type:</span><span>{entryRecord.carType==='open'?'◯ Open':'● Closed'}</span></div>
              <div className="receipt-row"><span>Staff:</span><span>{entryRecord.staffName}</span></div>
              {!entryRecord.tenantId&&!entryRecord.isMou&&entryRecord.packageType!=='overnight'&&(
                <><hr className="receipt-divider"/>
                <div className="receipt-row"><span>Hourly Rate:</span><span style={{fontWeight:700}}>AED {entryRecord.hourlyRate||settings.hourlyRate}/hour</span></div></>
              )}
              {entryRecord.packageType==='overnight'&&(
                <><hr className="receipt-divider"/>
                <div className="receipt-row"><span>Overnight Rate:</span><span style={{fontWeight:700}}>AED {entryRecord.overnightRate}</span></div>
                <div className="receipt-row"><span>Agreed Until:</span><span style={{fontWeight:700}}>{entryRecord.overnightEndTime}</span></div>
                {entryRecord.overnightAdvance>0&&(
                  <div className="receipt-row" style={{color:'#060',fontWeight:700}}><span>Advance Paid:</span><span>AED {entryRecord.overnightAdvance.toFixed(2)}</span></div>
                )}
                <div style={{fontSize:9,color:'#c00',marginTop:4,fontWeight:700}}>⚠️ Extra time after {entryRecord.overnightEndTime} charged at AED {settings.hourlyRate}/hr.</div></>
              )}
              <hr className="receipt-divider"/>
              <div style={{fontSize:9,color:'#333',lineHeight:1.6}}>
                <div style={{fontWeight:700,marginBottom:3,fontSize:10}}>TERMS & CONDITIONS</div>
                {entryRecord.packageType==='overnight'
                  ?<>
                    <div style={{color:'#c00',fontWeight:700}}>1. OVERNIGHT: Extra time beyond {entryRecord.overnightEndTime} charged at AED {settings.hourlyRate}/hr.</div>
                    <div>2. AED {settings.hourlyRate}/hr. First hour full rate.</div>
                    <div>3. Grace {settings.overtimeBufferMins}min = AED {settings.overtimeBufferFee} only. After {settings.overtimeBufferMins} mins full one hour (AED {settings.hourlyRate}) will be charged.</div>
                    <div>4. No liability for loss/damage/theft.</div>
                  </>
                  :<>
                    <div>1. AED {settings.hourlyRate}/hr. First hour full rate.</div>
                    <div>2. Grace {settings.overtimeBufferMins}min = AED {settings.overtimeBufferFee} only. After {settings.overtimeBufferMins} mins full one hour (AED {settings.hourlyRate}) will be charged.</div>
                    <div>3. No liability for loss/damage/theft.</div>
                  </>
                }
              </div>
              <hr className="receipt-divider"/>
              {entryQr&&<div style={{textAlign:'center',marginTop:6}}><img src={entryQr} alt="QR" style={{width:60,height:60}}/><div style={{fontSize:9,color:'#999',marginTop:2}}>Scan to verify</div></div>}
              <div style={{textAlign:'center',fontSize:9,color:'#666',marginTop:8}}>{settings.receiptFooter}</div>
            </div>

            {/* Change #1 — WhatsApp always shown, phone input if not entered */}
            <div style={{marginTop:12,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'12px'}}>
              <label className="form-label">📱 Send via WhatsApp</label>
              <div className="flex-row">
                <input className="form-input" type="tel" placeholder="+971 50 000 0000" value={receiptPhone} onChange={e=>setReceiptPhone(e.target.value)} style={{flex:1}}/>
                <button className="btn btn-whatsapp" onClick={()=>receiptPhone?sendWhatsApp(receiptPhone):alert('Enter phone number')} disabled={!receiptPhone}>💬 Send</button>
              </div>
            </div>

            {btAlert&&<div className={`alert ${btAlert.startsWith('✅')?'alert-success':'alert-danger'}`} style={{marginTop:8}}>{btAlert}</div>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:10}}>
              <button className="btn btn-ghost btn-full" onClick={()=>navigate('/dashboard')}><ArrowLeft size={13}/> Dashboard</button>
              <button className="btn btn-success btn-full" onClick={btPrint} disabled={btPrinting}>{btPrinting?'Printing...':'🖨️ BT Print'}</button>
              <button className="btn btn-outline btn-full" onClick={()=>window.print()}>🖨️ Browser Print</button>
              <button className="btn btn-primary btn-full" onClick={reset}>+ New Entry</button>
            </div>
          </div>
        )}

        {/* RENEWAL MODAL */}
        {showRenewModal&&(
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header"><div className="modal-title">🔄 Renew Package</div><button className="btn btn-ghost btn-sm" onClick={()=>setShowRenewModal(false)}>✕</button></div>
              <div className="modal-body">
                {alert&&<div className={`alert alert-${alert.type}`}>{alert.msg}</div>}
                <div style={{background:'var(--bg-input)',padding:'12px',borderRadius:'var(--radius-sm)',marginBottom:12}}>
                  <div style={{fontWeight:600}}>{tenantData?.name}</div>
                  <div style={{fontSize:12,color:'var(--danger)',marginTop:4}}>⛔ Expired: {fmtDate(tenantData?.expiryDate)}</div>
                </div>
                <div className="alert alert-info" style={{fontSize:12}}><Info size={13}/>Select package type and new expiry date. Entry proceeds automatically after renewal.</div>
                <div className="form-group">
                  <label className="form-label">Package Type</label>
                  <select className="form-select" value={renewPkgType||tenantData?.packageType||'monthly-24hr'} onChange={e=>setRenewPkgType(e.target.value)}>
                    {PKG_TYPE_TENANT.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">New Expiry Date *</label>
                  <input className="form-input" type="date" min={minExpiry()} value={renewExpiryDate} onChange={e=>setRenewExpiryDate(e.target.value)}/>
                  {renewExpiryDate&&<div style={{fontSize:11,color:'var(--success)',marginTop:4}}>New expiry: {fmtDate(renewExpiryDate)}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={()=>setShowRenewModal(false)}>Cancel</button>
                <button className="btn btn-success" onClick={handleRenewal} disabled={submitting}>{submitting?'⏳ Saving...':'✅ Renew & Allow Entry'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
